import { MapPin, Plus, Trash2 } from 'lucide-react';
import { nanoid } from 'nanoid';
import type React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useIntl } from '@/core/i18n/use-intl';
import type { Position } from '@/shared/schema';
import { cn } from '@/shared/utils';
import { Button } from '../../button';
import { Card, CardContent } from '../../card';
import { Checkbox } from '../../checkbox';
import { FormItem, FormMessage } from '../../form';
import { Input } from '../../input';
import { Label } from '../../label';
import AutoFormLabel from '../common/label';
import type { AutoFormInputComponentProps } from '../types';

/**
 * Attempts to parse a coordinate string that may contain both lat and long.
 */
function tryParseCoordinatePair(input: string): [string, string] | null {
  const trimmed = input.trim();
  const normalized = trimmed
    // Normalize smart quotes/primes used as minutes/seconds markers
    .replace(/[\u2018\u2019\u2032]/g, "'")
    .replace(/[\u201C\u201D\u2033]/g, '"');

  // Pattern 1: Decimal degrees (e.g., "54.79076, 9.42546")
  const decimalPattern = /^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/;
  const decimalMatch = normalized.match(decimalPattern);
  if (decimalMatch) {
    return [decimalMatch[1].trim(), decimalMatch[2].trim()];
  }

  // Pattern 2: Degree notation - split on comma AFTER closing apostrophe
  const degreeCommaPattern = /^(.+['"])\s*,\s*(\d.*)$/;
  const degreeCommaMatch = normalized.match(degreeCommaPattern);
  if (degreeCommaMatch) {
    return [degreeCommaMatch[1].trim(), degreeCommaMatch[2].trim()];
  }

  // Pattern 3: Space-separated degree notation
  const degreeSpacePattern = /^(.+['"])\s+(\d.*)$/;
  const degreeSpaceMatch = normalized.match(degreeSpacePattern);
  if (degreeSpaceMatch) {
    return [degreeSpaceMatch[1].trim(), degreeSpaceMatch[2].trim()];
  }

  return null;
}

/**
 * Ensures a value is always a valid Position array.
 */
function ensurePositionsArray(value: unknown): Position[] {
  if (!value || !Array.isArray(value)) {
    return [];
  }
  return value
    .filter((item): item is Position => {
      return item && typeof item === 'object' && typeof item.id === 'string';
    })
    .map((pos) => ({
      id: pos.id,
      label: pos.label ?? '',
      latitude: pos.latitude ?? '',
      longitude: pos.longitude ?? '',
      status: pos.status ?? false,
    }));
}

/**
 * Creates a new empty position entry with a unique ID.
 */
function createEmptyPosition(showStatus: boolean): Position {
  return {
    id: nanoid(8),
    label: '',
    latitude: '',
    longitude: '',
    ...(showStatus ? { status: false } : {}),
  };
}

interface PositionInputProps {
  id: string;
  testId: string;
  initialValue: string;
  onValueChange: (value: string) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  disabled?: boolean;
  label: string;
  labelClassName?: string;
  inputClassName?: string;
}

/**
 * Truly uncontrolled input that manages its own DOM state.
 * Parent updates are only applied when the input is not focused.
 */
const PositionInput = memo(function PositionInput({
  id,
  testId,
  initialValue,
  onValueChange,
  onPaste,
  placeholder,
  disabled,
  label,
  labelClassName,
  inputClassName,
}: PositionInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const lastReportedValue = useRef(initialValue);

  // Apply initial value on mount
  useEffect(() => {
    if (inputRef.current && inputRef.current.value === '') {
      inputRef.current.value = initialValue;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update from parent only when not focused
  useEffect(() => {
    if (inputRef.current && document.activeElement !== inputRef.current) {
      if (inputRef.current.value !== initialValue) {
        inputRef.current.value = initialValue;
        lastReportedValue.current = initialValue;
      }
    }
  }, [initialValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (newValue !== lastReportedValue.current) {
        lastReportedValue.current = newValue;
        onValueChange(newValue);
      }
    },
    [onValueChange]
  );

  return (
    <div className="space-y-1">
      <Label htmlFor={id} className={cn('text-xs', labelClassName)}>
        {label}
      </Label>
      <Input
        ref={inputRef}
        id={id}
        data-testid={testId}
        defaultValue={initialValue}
        onChange={handleChange}
        onPaste={onPaste}
        placeholder={placeholder}
        disabled={disabled}
        className={cn('h-9 md:h-8', inputClassName)}
      />
    </div>
  );
});

interface PositionStatusProps {
  id: string;
  testId: string;
  initialChecked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  statusLabel: string;
  labelClassName?: string;
}

/**
 * Uncontrolled checkbox for status.
 */
const PositionStatus = memo(function PositionStatus({
  id,
  testId,
  initialChecked,
  onCheckedChange,
  disabled,
  statusLabel,
  labelClassName,
}: PositionStatusProps) {
  const handleChange = useCallback(
    (checked: boolean | 'indeterminate') => {
      onCheckedChange(checked === true);
    },
    [onCheckedChange]
  );

  return (
    <div className="flex items-center gap-2 pt-1 md:pt-0 md:justify-center">
      <Checkbox
        id={id}
        data-testid={testId}
        defaultChecked={initialChecked}
        onCheckedChange={handleChange}
        disabled={disabled}
      />
      <Label htmlFor={id} className={cn('text-sm cursor-pointer', labelClassName)}>
        {statusLabel}
      </Label>
    </div>
  );
});

interface PositionRowProps {
  position: Position;
  index: number;
  onUpdate: (id: string, updates: Partial<Position>) => void;
  onRemove: (id: string) => void;
  onCoordinatePaste: (id: string, lat: string, lng: string) => void;
  disabled?: boolean;
  showStatus?: boolean;
  statusLabel?: string;
}

/**
 * Memoized position row component.
 */
const PositionRow = memo(function PositionRow({
  position,
  index,
  onUpdate,
  onRemove,
  onCoordinatePaste,
  disabled,
  showStatus,
  statusLabel,
}: PositionRowProps) {
  const { t } = useIntl();
  const positionId = position.id;
  const gridCols = showStatus
    ? 'md:grid-cols-[1.4fr_1fr_1fr_minmax(10rem,0.8fr)_auto]'
    : 'md:grid-cols-[1.4fr_1fr_1fr_auto]';

  const handleLabelChange = useCallback(
    (value: string) => {
      onUpdate(positionId, { label: value });
    },
    [positionId, onUpdate]
  );

  const handleLatitudeChange = useCallback(
    (value: string) => {
      onUpdate(positionId, { latitude: value });
    },
    [positionId, onUpdate]
  );

  const handleLongitudeChange = useCallback(
    (value: string) => {
      onUpdate(positionId, { longitude: value });
    },
    [positionId, onUpdate]
  );

  const handleStatusChange = useCallback(
    (checked: boolean) => {
      onUpdate(positionId, { status: checked });
    },
    [positionId, onUpdate]
  );

  const handleRemove = useCallback(() => {
    onRemove(positionId);
  }, [positionId, onRemove]);

  const handleLatPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');
      const parsed = tryParseCoordinatePair(pastedText);
      if (parsed) {
        e.preventDefault();
        // Directly set the DOM value of the focused input since it won't update from state
        const target = e.target as HTMLInputElement;
        target.value = parsed[0];
        onCoordinatePaste(positionId, parsed[0], parsed[1]);
      }
    },
    [positionId, onCoordinatePaste]
  );

  const handleLongPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');
      const parsed = tryParseCoordinatePair(pastedText);
      if (parsed) {
        e.preventDefault();
        // Directly set the DOM value of the focused input since it won't update from state
        const target = e.target as HTMLInputElement;
        target.value = parsed[1];
        onCoordinatePaste(positionId, parsed[0], parsed[1]);
      }
    },
    [positionId, onCoordinatePaste]
  );

  return (
    <div
      className="grid gap-2 p-2 rounded-lg border bg-muted/20"
      data-testid={`position-row-${index}`}
    >
      <div className={cn('grid gap-2 md:items-center', gridCols)}>
        <PositionInput
          id={`pos-${positionId}-label`}
          testId={`position-${index}-label`}
          initialValue={position.label ?? ''}
          onValueChange={handleLabelChange}
          placeholder={t('metadata.positions.labelPlaceholder')}
          disabled={disabled}
          label={t('metadata.positions.label')}
          labelClassName="md:hidden"
        />

        <PositionInput
          id={`pos-${positionId}-lat`}
          testId={`position-${index}-latitude`}
          initialValue={position.latitude ?? ''}
          onValueChange={handleLatitudeChange}
          onPaste={handleLatPaste}
          placeholder={t('metadata.positions.latitudePlaceholder')}
          disabled={disabled}
          label={t('metadata.positions.latitude')}
          labelClassName="md:hidden"
        />

        <PositionInput
          id={`pos-${positionId}-long`}
          testId={`position-${index}-longitude`}
          initialValue={position.longitude ?? ''}
          onValueChange={handleLongitudeChange}
          onPaste={handleLongPaste}
          placeholder={t('metadata.positions.longitudePlaceholder')}
          disabled={disabled}
          label={t('metadata.positions.longitude')}
          labelClassName="md:hidden"
        />

        {showStatus && statusLabel && (
          <div className="flex items-center justify-center md:h-8">
            <PositionStatus
              id={`pos-${positionId}-status`}
              testId={`position-${index}-status`}
              initialChecked={position.status ?? false}
              onCheckedChange={handleStatusChange}
              disabled={disabled}
              statusLabel={statusLabel}
              labelClassName="md:hidden"
            />
          </div>
        )}

        <div className="flex items-end justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            disabled={disabled}
            data-testid={`position-${index}-remove`}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
});

/**
 * AutoFormPositions - A form field component for managing multiple position entries.
 *
 * Architecture:
 * - Local state (positions) is the source of truth for rendering
 * - Changes are immediately synced to form via field.onChange
 * - External changes (form reset, initial load) are detected by comparing field.value IDs
 * - Input components are uncontrolled to prevent focus loss
 */
export default function AutoFormPositions({
  label,
  isRequired,
  fieldConfigItem,
  field,
  fieldProps,
}: AutoFormInputComponentProps & {
  field: ControllerRenderProps<FieldValues, string>;
}) {
  const { t, locale } = useIntl();
  const { showLabel: _showLabel, disabled, ...restFieldProps } = fieldProps;
  const showLabel = _showLabel === undefined ? true : _showLabel;

  // Extract status label from field config if available
  const statusLabel =
    locale === 'de-DE'
      ? (fieldConfigItem?.statusLabel as string | undefined)
      : (fieldConfigItem?.statusLabelEn as string | undefined) ||
        (fieldConfigItem?.statusLabel as string | undefined);
  const showStatus = !!statusLabel;
  const headerGridCols = showStatus
    ? 'md:grid-cols-[1.4fr_1fr_1fr_minmax(10rem,0.8fr)_auto]'
    : 'md:grid-cols-[1.4fr_1fr_1fr_auto]';

  // Local state is THE source of truth for rendering
  const [positions, setPositions] = useState<Position[]>(() => ensurePositionsArray(field.value));

  // Track whether the current update originated from us
  const isOwnUpdateRef = useRef(false);

  // Compute current position IDs from field.value for detecting external changes
  const fieldValueIds = useMemo(() => {
    const arr = ensurePositionsArray(field.value);
    return arr.map((p) => p.id).join(',');
  }, [field.value]);

  // Compute current local position IDs
  const localIds = useMemo(() => positions.map((p) => p.id).join(','), [positions]);

  // Sync FROM form when IDs change (external update like form reset)
  useEffect(() => {
    if (isOwnUpdateRef.current) {
      isOwnUpdateRef.current = false;
      return;
    }

    // Only sync if the structure (IDs) changed
    if (fieldValueIds !== localIds) {
      const newPositions = ensurePositionsArray(field.value);
      setPositions(newPositions);
    }
  }, [fieldValueIds, localIds, field.value]);

  // Sync TO form - called synchronously when local state changes
  const syncToForm = useCallback(
    (newPositions: Position[]) => {
      const cleaned = newPositions.map((pos) => ({
        id: pos.id,
        label: pos.label ?? '',
        latitude: pos.latitude ?? '',
        longitude: pos.longitude ?? '',
        ...(showStatus ? { status: pos.status ?? false } : {}),
      }));
      const formValue = cleaned.length > 0 ? cleaned : undefined;

      isOwnUpdateRef.current = true;
      field.onChange(formValue);
    },
    [field, showStatus]
  );

  // Update a position's fields
  const handleUpdate = useCallback(
    (id: string, updates: Partial<Position>) => {
      setPositions((prev) => {
        const newPositions = prev.map((pos) => (pos.id === id ? { ...pos, ...updates } : pos));
        syncToForm(newPositions);
        return newPositions;
      });
    },
    [syncToForm]
  );

  // Smart paste handler
  const handleCoordinatePaste = useCallback(
    (id: string, lat: string, lng: string) => {
      setPositions((prev) => {
        const newPositions = prev.map((pos) =>
          pos.id === id ? { ...pos, latitude: lat, longitude: lng } : pos
        );
        syncToForm(newPositions);
        return newPositions;
      });
    },
    [syncToForm]
  );

  // Add position
  const handleAddPosition = useCallback(() => {
    setPositions((prev) => {
      const newPosition = createEmptyPosition(showStatus);
      const newPositions = [...prev, newPosition];
      syncToForm(newPositions);
      return newPositions;
    });
  }, [showStatus, syncToForm]);

  // Remove position
  const handleRemovePosition = useCallback(
    (id: string) => {
      setPositions((prev) => {
        const newPositions = prev.filter((p) => p.id !== id);
        syncToForm(newPositions);
        return newPositions;
      });
    },
    [syncToForm]
  );

  return (
    <FormItem className="col-span-6" {...restFieldProps}>
      <Card className="border-border/60" data-testid="positions-field">
        <CardContent className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            {showLabel && (
              <div className="flex items-center gap-2 pt-1">
                <MapPin className="size-4 text-muted-foreground" />
                <AutoFormLabel
                  label={fieldConfigItem?.label || label}
                  isRequired={isRequired}
                  className="text-sm font-medium"
                />
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddPosition}
              disabled={disabled}
              data-testid="add-position-button"
            >
              <Plus className="size-4 mr-2" />
              {t('metadata.positions.addPosition')}
            </Button>
          </div>
          {positions.length === 0 ? (
            <p
              className="text-sm text-muted-foreground py-4 text-center"
              data-testid="no-positions-message"
            >
              {t('metadata.positions.noPositions')}
            </p>
          ) : (
            <div className="space-y-3">
              <div className={cn('hidden md:grid gap-2 px-2', headerGridCols)}>
                <span className="text-xs font-medium text-muted-foreground pl-0">
                  {t('metadata.positions.label')}
                </span>
                <span className="text-xs font-medium text-muted-foreground pl-1">
                  {t('metadata.positions.latitude')}
                </span>
                <span className="text-xs font-medium text-muted-foreground pl-2">
                  {t('metadata.positions.longitude')}
                </span>
                {showStatus && (
                  <span className="text-xs font-medium text-muted-foreground px-3">
                    {statusLabel}
                  </span>
                )}
                <span className="text-xs font-medium text-muted-foreground text-right">
                  {t('common.actions')}
                </span>
              </div>
              {positions.map((position, index) => (
                <PositionRow
                  key={position.id}
                  position={position}
                  index={index}
                  onUpdate={handleUpdate}
                  onRemove={handleRemovePosition}
                  onCoordinatePaste={handleCoordinatePaste}
                  disabled={disabled}
                  showStatus={showStatus}
                  statusLabel={statusLabel}
                />
              ))}
            </div>
          )}

          <FormMessage />
        </CardContent>
      </Card>
    </FormItem>
  );
}
